import { omit } from "lodash";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";

import { hasEligibilityResponse } from "./noDeductibleChoice";

/**
 * 2026-10-27 (Ani) — "No applicable copay found" / "No applicable co-insurance found" on Flat Copay and Coinsurance
 * Percent (primary insurance). The CLE-2996 "No deductible" choice (`noDeductibleChoice.ts`), applied to the two cost
 * shares, wired the same way in every place.
 *
 * The benefit engine answers "not applicable" when the payer reported the copay / co-insurance on the account's own care
 * setting and this account's own rules (its ignore list, payer filter or selection rule priorities) excluded every line
 * it was reported on — Ani: "we don't trigger historical if our rules are the ones removing it" … "N/A is correct
 * answer". The API then sends `copayNotApplicable` / `coinsuranceNotApplicable` beside a real 0.
 *  - ON: the field holds a real 0, the box is left empty with the placeholder, and the saved estimate records
 *    `benefitDetails.noApplicableCopay` / `.noApplicableCoinsurance: true`, so it can be told from a typed 0 later.
 *  - OFF: the record is gone. The box stays editable while ON: typing a number in it withdraws the choice and the typed
 *    number is the value; deleting it back to empty leaves it BLANK.
 *
 * Set ONLY by the payer's response (each new response re-applies it), or restored from a saved estimate on draft / copy /
 * redo (`readNotApplicableChoices`). Lives in `Order.benefitDetails` with the deductible's record, so it can be on only
 * while an eligibility response is on the form.
 */

export type NotApplicableBenefit = "flatCopay" | "coinsurancePercent";

interface INotApplicableChoice {
  /** The form field (and the saved `benefitDetails` key) that records the choice. */
  choiceField: "noApplicableCopay" | "noApplicableCoinsurance";
  /** The eligibility response flag that turns it on. */
  responseFlag: "copayNotApplicable" | "coinsuranceNotApplicable";
  placeholder: string;
}

export const NOT_APPLICABLE_CHOICES: Record<
  NotApplicableBenefit,
  INotApplicableChoice
> = {
  flatCopay: {
    choiceField: "noApplicableCopay",
    responseFlag: "copayNotApplicable",
    placeholder: "No applicable copay found",
  },
  coinsurancePercent: {
    choiceField: "noApplicableCoinsurance",
    responseFlag: "coinsuranceNotApplicable",
    placeholder: "No applicable co-insurance found",
  },
};

const BENEFITS = Object.keys(NOT_APPLICABLE_CHOICES) as NotApplicableBenefit[];

interface IFormAccess {
  setValue: UseFormSetValue<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
}

export const setNotApplicableChoice = (
  { setValue, getValues }: IFormAccess,
  benefit: NotApplicableBenefit,
  on: boolean
): void => {
  const { choiceField } = NOT_APPLICABLE_CHOICES[benefit];
  if (on) {
    setValue(choiceField, true);
    setValue(benefit, 0);
    return;
  }
  // Only a choice that was on owns the 0 in the box; an estimator's own typed number is never cleared.
  if (getValues(choiceField) === true) {
    setValue(choiceField, false);
    setValue(benefit, null);
  }
};

/** Re-applies the engine's answer from a new eligibility response (primary side only). */
export const applyEligibilityNotApplicable = (
  form: IFormAccess,
  response: Record<string, any> | null | undefined
): void =>
  BENEFITS.forEach((benefit) =>
    setNotApplicableChoice(
      form,
      benefit,
      response?.[NOT_APPLICABLE_CHOICES[benefit].responseFlag] === true
    )
  );

/** The choices as a saved estimate recorded them — for draft / copy / redo. */
export const readNotApplicableChoices = (
  benefitDetails: any
): { noApplicableCopay: boolean; noApplicableCoinsurance: boolean } => {
  const recorded = (benefit: NotApplicableBenefit): boolean =>
    hasEligibilityResponse(benefitDetails) &&
    benefitDetails[NOT_APPLICABLE_CHOICES[benefit].choiceField] === true;
  return {
    noApplicableCopay: recorded("flatCopay"),
    noApplicableCoinsurance: recorded("coinsurancePercent"),
  };
};

/**
 * The `benefitDetails` to save: each record is added while its choice is on, and removed otherwise (a draft reopened with
 * a choice on, then switched off, must not keep it).
 */
export const withNotApplicableRecords = (
  benefitDetails: any,
  choices: { noApplicableCopay?: unknown; noApplicableCoinsurance?: unknown }
): any => {
  if (!hasEligibilityResponse(benefitDetails)) return benefitDetails;
  const rest = omit(benefitDetails, [
    "noApplicableCopay",
    "noApplicableCoinsurance",
  ]);
  return {
    ...rest,
    ...(choices.noApplicableCopay === true ? { noApplicableCopay: true } : {}),
    ...(choices.noApplicableCoinsurance === true
      ? { noApplicableCoinsurance: true }
      : {}),
  };
};
